/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

'use strict';

let appModule = angular.module('prism-ui', [
    'prism-ui.assetContentTemplates',
    'prism-ui.calendars',
    'prism-ui.categories',
    'prism-ui.collaborators',
    'prism-ui.components',
    'prism-ui.components.branding',
    'prism-ui.components.constants',
    'prism-ui.components.navigation',
    'prism-ui.conditionalTemplate',
    'prism-ui.config',
    'prism-ui.content',
    'prism-ui.dashboard.checkin',
    'prism-ui.deliveryTemplates',
    'prism-ui.deliveryProfiles',
    'prism-ui.execServers',
    'prism-ui.emailTemplates',
    'prism-ui.externalMetadata',
    'prism-ui.fileSystem',
    'prism-ui.ffmpegTemplate',
    'prism-ui.genres',
    'prism-ui.groups',
    'prism-ui.imageTemplate',
    'prism-ui.intermediateTemplate',
    'prism-ui.languages',
    'prism-ui.livefeed',
    'prism-ui.login',
    'prism-ui.mediaAcceptProfiles',
    'prism-ui.metadataItems',
    'prism-ui.metricsDashboard',
    'prism-ui.mvpd',
    'prism-ui.network',
    'prism-ui.nielsenTemplates',
    'prism-ui.offCyclePlanners',
    'prism-ui.package',
    'prism-ui.packageAcceptTemplate',
    'prism-ui.packageCreationTemplate',
    'prism-ui.packageNotifications',
    'prism-ui.partner',
    'prism-ui.properties',
    'prism-ui.queues',
    'prism-ui.report',
    'prism-ui.roles',
    'prism-ui.system.maccaptionTemplate',
    'prism-ui.templates',
    'prism-ui.territories',
    'prism-ui.transformTemplate',
    'prism-ui.users',
    'prism-ui.workflows',
    'angularFileUpload',
    'angularMoment',
    // 'levelsBeyond.ui.progress',
    'ngAnimate',
    'ngRoute',
    'reachEngine.authentication',
    'reachEngine.user',
    'react',
    'templates-app',
    'ui.bootstrap',
]);

const canIgnoreResponseError = function (rejection) {
    try {
        var rejectionUrl = new URL(rejection.config.url);
        return (
            rejectionUrl.pathname === '/ping' ||
            rejectionUrl.pathname.match('^/reachengine/api/events/\\d+$')
        );
    } catch (e) {
        return false;
    }
};

//
// Returns an object describing our HTTP error response
// interceptor.
//
const failureInterceptor = (
    $location,
    $q,
    $rootScope
) => {
    return {
        responseError: function (rejection) {
            if (rejection.status === 401) {
                $rootScope.session.clear();
                $location.path('/login');
            } else if (rejection.status >= 400 && rejection.status <= 599) {
                if (canIgnoreResponseError(rejection)) {
                    return $q.reject(rejection);
                }
                var message = (rejection.data && rejection.data.message)?
                    rejection.data.message:
                    rejection.statusText;
                $rootScope.$broadcast('server-error', {message: message});
            }
            $rootScope.$broadcast('server-busy', {working: false});
            return $q.reject(rejection);
        }
    };
};

function configInterceptors($httpProvider, jwtOptionsProvider) {
    // overwrite A.R.E.S. jwt config
    jwtOptionsProvider.config({
        tokenGetter: (jwtStore) => jwtStore.get(),
        whiteListedDomains: ['relocal', 'localhost']
    });

    $httpProvider.interceptors.push(failureInterceptor);
}

appModule.config(configInterceptors);

function afterConfig(
    $location,
    $rootScope,
    $route,
    $routeParams,
    configService,
    loginService,
    BreadcrumbService,
    EndPointService,
    ModalService,
    NotificationService,
    SearchFilterService,
    SidebarService
) {
    configService.setReachEngineUrl(EndPointService.reachengineServerUrl);

    $rootScope.$on('$routeChangeStart', function (event) {
        let path = $location.path();

        ModalService.closeAllModals();

        if (!loginService.isAuthenticated()) {
            BreadcrumbService.clearBreadcrumbs();

            // look for "chromeless" (no login) paths
            switch (path) {
                case '/dashboard/metrics':
                case '/login':
                    break;

                default:
                    // All Others
                    localStorage.preLoginRoute = $location.url();
                    $location.url('/login');
                    break;
            }

            return;
        }


        let routeChangeHandlerTable = [

            //
            // Each object corresponds to one route change handler.
            // The objects have the following properties:
            //
            //  path - the route's base path
            //
            //  sidebarItem - the name of the sidebar item which
            //      will be passed to
            //      SidebarService.setSelectedItem()
            //
            //  baseBreadcrumbPath - the name of the topmost
            //      subpath (e.g. '/search) that will correspond
            //      to the base breadcrumb
            //
            //  hasFilterParams - true if the route will use
            //      $location.search() to track search criteria
            //      for a filter (such as workflows or packages)
            //

            //
            // NOTICE!
            //
            // 1) These are organized in the order in which they
            // appear on the app sidebar. Please keep it that way!
            //
            // 2) Please also keep the corresponding table in
            // app_test.js up-to-date. Difficult-to-trace errors
            // can be avoided by having the unit tests check the
            // route change handlers.
            //
            // Thanks! :D
            //

            // DASHBOARD
            {path: '/dashboard/workflows', sidebarItem: 'workflows', baseBreadcrumbPath: '', hasFilterParams: true},
            {path: '/dashboard/queues', sidebarItem: 'queues', baseBreadcrumbPath: '', hasFilterParams: true},
            {path: '/dashboard/report', sidebarItem: 'reports', baseBreadcrumbPath: '', hasFilterParams: false},
            {path: '/dashboard/calendars', sidebarItem: 'calendars', baseBreadcrumbPath: '', hasFilterParams: false},
            {path: '/dashboard/offCyclePlanners', sidebarItem: 'offCyclePlanners', baseBreadcrumbPath: '', hasFilterParams: false},
            {path: '/dashboard/checkin', sidebarItem: 'checkin', baseBreadcrumbPath: '', hasFilterParams: false},
            {path: '/dashboard/metrics', sidebarItem: 'metrics', baseBreadcrumbPath: '', hasFilterParams: false},

            // META
            {path: '/meta/networks', sidebarItem: 'networks', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/meta/adsAndPreRolls', sidebarItem: 'adsAndPreRolls', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/meta/series', sidebarItem: 'series', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/meta/movies', sidebarItem: 'movies', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/meta/collaborators', sidebarItem: 'collaborators', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/meta/mvpd', sidebarItem: 'mvpd', baseBreadcrumbPath: '/search', hasFilterParams: false},

            // SYSTEM
            {path: '/system/partners', sidebarItem: 'partners', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/system/properties', sidebarItem: 'properties', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/system/templates', sidebarItem: 'templates', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/system/metadataItems', sidebarItem: 'metadataItems', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/system/livefeeds', sidebarItem: 'livefeeds', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/system/fileSystems', sidebarItem: 'fileSystems', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/system/aggregation', sidebarItem: 'aggregation', baseBreadcrumbPath: '/setup', hasFilterParams: false},
            {path: '/system/execServers', sidebarItem: 'execServers', baseBreadcrumbPath: '', hasFilterParams: false},
            {path: '/system/packageNotifications', sidebarItem: 'packageNotifications', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/system/users', sidebarItem: 'users', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/system/groups', sidebarItem: 'groups', baseBreadcrumbPath: '/search', hasFilterParams: false},
            {path: '/system/contacts', sidebarItem: 'contacts', baseBreadcrumbPath: '/search', hasFilterParams: false},

            // PACKAGES
            {path: '/packages', sidebarItem: 'packages', baseBreadcrumbPath: '', hasFilterParams: true},
        ];

        let handled = false;
        angular.forEach(routeChangeHandlerTable, (routeChangeHandler) => {
            if (path.startsWith(routeChangeHandler.path)) {
                handled = true;
                SidebarService.setSelectedItem(routeChangeHandler.sidebarItem);
                if (path === routeChangeHandler.path + routeChangeHandler.baseBreadcrumbPath) {
                    BreadcrumbService.clearBreadcrumbs();
                    if (routeChangeHandler.hasFilterParams) {
                        SearchFilterService.updateLocationSearch();
                    }
                }
            }
        });

        // weird routes get sent back to the package manager
        if (!handled) {
            event.preventDefault();
            BreadcrumbService.clearBreadcrumbs();
            $location.path('/packages');
            $location.search('pmView', 'basic');
        }
    });

    $rootScope.$on('server-error', function (event, error) {
        NotificationService.createFailureNotification(error.message, '', true, -1);
    });

    $rootScope.session = {
        clear: function () {
            loginService.deleteSessionData();
        }
    };

}

appModule.factory('AuthFactory', (jwtStore) => {
    const authQueryParamName = 'auth';

    return {
        getAuthQueryString: () => `${authQueryParamName}=${jwtStore.get()}`,
        getAuthHeader: () => `Bearer ${jwtStore.get()}`
    };

});

appModule.run(afterConfig);
